import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'

type Category = 'marketing' | 'game'

interface Preferences {
  marketing: boolean
  game: boolean
}

export function EmailPreferences() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const queryOptions = trpc.emailPreferencesRouter.get.queryOptions()
  const { data, isLoading } = useQuery(queryOptions)

  const setPreference = useMutation({
    ...trpc.emailPreferencesRouter.set.mutationOptions(),
    onSuccess: (next: Preferences) => {
      queryClient.setQueryData(queryOptions.queryKey, next)
    },
  })

  const rows: Array<{
    category: Category
    label: string
    hint: string
    warning?: string
  }> = [
    {
      category: 'marketing',
      label: t('auth.dashboard.emailPrefsMarketing'),
      hint: t('auth.dashboard.emailPrefsMarketingHint'),
    },
    {
      category: 'game',
      label: t('auth.dashboard.emailPrefsGame'),
      hint: t('auth.dashboard.emailPrefsGameHint'),
      warning: t('auth.dashboard.emailPrefsGameWarning'),
    },
  ]

  return (
    <Card data-testid="email-preferences">
      <CardHeader>
        <CardTitle>{t('auth.dashboard.emailPrefsTitle')}</CardTitle>
        <CardDescription>
          {t('auth.dashboard.emailPrefsDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading
          ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
              </div>
            )
          : (
              rows.map((row) => {
                const subscribed = data ? data[row.category] : true
                const pending
                  = setPreference.isPending
                    && setPreference.variables?.category === row.category
                return (
                  <div key={row.category} className="flex items-start gap-3">
                    <Checkbox
                      id={`email-pref-${row.category}`}
                      data-testid={`email-pref-${row.category}`}
                      className="mt-1"
                      checked={subscribed}
                      disabled={pending}
                      onCheckedChange={(checked) => {
                        setPreference.mutate({
                          category: row.category,
                          subscribed: checked === true,
                        })
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor={`email-pref-${row.category}`}
                        className="cursor-pointer text-sm font-medium"
                      >
                        {row.label}
                      </label>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {row.hint}
                      </p>
                      {
                      }
                      {row.warning && !subscribed && (
                        <p className="mt-2 border-l-2 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                          {row.warning}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {subscribed
                          ? t('auth.dashboard.emailPrefsSubscribed')
                          : t('auth.dashboard.emailPrefsUnsubscribed')}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
        {setPreference.isError && (
          <p className="text-sm text-destructive">
            {t('auth.dashboard.emailPrefsSaveError')}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
